/**
 * RequestingList Component
 * Displays list of transfer bond requests with status-based tabs
 * Groups data by agency (matches legacy requestLandingpage.jsx)
 * Pure Tailwind - Table on desktop, card view on mobile
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  XCircleIcon,
  CheckCircleIcon,
  UserIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import {
  getTransferBondRequests,
  getCancelledRequests,
  getClosedRequests,
} from '../../../services/oathTransferBondService';
import usePermissions from '../../../hooks/usePermissions';

// Format currency
const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// Format date
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Format date + time (matches legacy stacked date/time rendering)
const formatDateTime = (value) => {
  if (!value) return { date: '-', time: '' };
  const d = new Date(value);
  return {
    date: d.toLocaleDateString('en-US'),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

// Hook for detecting mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// Loading skeleton for mobile
const MobileLoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2" />
          </div>
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-20" />
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div><div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16 mb-1" /><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24" /></div>
          <div><div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16 mb-1" /><div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" /></div>
        </div>
      </div>
    ))}
  </div>
);

// Loading skeleton for desktop table
const TableLoadingSkeleton = () => (
  <div className="animate-pulse">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-4 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-12" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded flex-1" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20" />
      </div>
    ))}
  </div>
);

// Sortable header component
const SortableHeader = ({ label, field, sortField, sortDirection, onSort, align = 'left' }) => {
  const isActive = sortField === field;
  const alignClass = align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${alignClass}`}>
        <span>{label}</span>
        <span className="text-gray-400 dark:text-gray-500">
          {isActive ? (
            sortDirection === 'asc' ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )
          ) : (
            <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
          )}
        </span>
      </div>
    </th>
  );
};

// Agency group header (matches legacy grouping by dbaAgencyName)
const AgencyGroupHeader = ({ group, showDetails = false }) => (
  <div className="mb-2 mt-4 first:mt-0">
    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
      {group.dbaAgencyName}{group.agentId ? ` (${group.agentId})` : ''}
    </h3>
    {showDetails && group.primaryContact && (
      <p className="text-sm text-gray-600 dark:text-gray-400">Primary Contact: {group.primaryContact}</p>
    )}
  </div>
);

// Per-group pagination (matches legacy DataGrid per-group pagination at pageSize 10)
const GroupPagination = ({ currentPage, totalPages, totalRows, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRows);

  return (
    <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Showing {start} to {end} of {totalRows}
      </p>
      <div className="flex items-center gap-2">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="p-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs text-gray-600 dark:text-gray-400">{currentPage} / {totalPages}</span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          className="p-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Open Tab: Card + Row components
// Matches legacy requestLandingpage.jsx columns:
//   Ref#, Created At, #Bonds, Amount, Defendant, Posting Agency*, Status, County, State
// ============================================================

const OpenRequestCard = ({ row, onView, onCancel, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';

  const renderStatus = () => {
    if (row.hasBeenPosted) {
      return (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-green-700 dark:text-green-400">
          Verification <CheckCircleIcon className="h-4 w-4" />
        </span>
      );
    }
    if (row.executingAgentID) {
      return (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-green-700 dark:text-green-400">
          Agent Posting <UserIcon className="h-4 w-4" />
        </span>
      );
    }
    if (row.numberOfQuotes >= 1) {
      return (
        <span className="text-sm italic text-gray-600 dark:text-gray-400">
          <strong>{row.numberOfQuotes}</strong> quote(s) pending
        </span>
      );
    }
    return <span className="text-sm italic text-gray-400 dark:text-gray-500">waiting for quotes...</span>;
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
      onClick={() => onView(row)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }}
            className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors" aria-label="View details">
            <EyeIcon className="h-5 w-5" />
          </button>
          {!row.executingAgentID && onCancel && (
            <button onClick={(e) => { e.stopPropagation(); onCancel(row); }}
              className="p-2 text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors" aria-label="Cancel request">
              <XCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      <div className="mb-3">{renderStatus()}</div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Amount</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Bonds</p><p className="text-sm font-medium text-gray-900 dark:text-white">{row.numberOfBonds || '-'}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Created</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(row.createdAt)}</p></div>
        {canViewPostingAgency && row.executingAgentID && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Posting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white">{row.postingAgencyName || '-'}</p></div>
        )}
      </div>
    </div>
  );
};

const OpenTableRow = ({ row, onView, onCancel, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const { date, time } = formatDateTime(row.createdAt);

  const renderStatus = () => {
    if (row.hasBeenPosted) return <span className="font-medium text-green-700 dark:text-green-400">Verification &#10003;</span>;
    if (row.executingAgentID) return <span className="font-medium text-green-700 dark:text-green-400">Agent Posting</span>;
    if (row.numberOfQuotes >= 1) return <span className="italic"><strong>{row.numberOfQuotes}</strong> quote(s)</span>;
    return <span className="italic text-gray-500 dark:text-gray-400">waiting...</span>;
  };

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        <div className="leading-tight">{date}</div>
        {time && <div className="text-xs text-gray-400 dark:text-gray-500">{time}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{row.numberOfBonds || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[180px] truncate">{defendantName}</td>
      {canViewPostingAgency && (
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">
          {row.executingAgentID ? (row.postingAgencyName || '-') : '-'}
        </td>
      )}
      <td className="px-4 py-3 text-sm">{renderStatus()}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{row.county || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{row.state || '-'}</td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onView(row); }}
            className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
            <EyeIcon className="h-5 w-5" />
          </button>
          {!row.executingAgentID && onCancel && (
            <button onClick={(e) => { e.stopPropagation(); onCancel(row); }}
              className="p-1.5 text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Cancel Request">
              <XCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ============================================================
// Cancelled Tab: Card + Row components
// Matches legacy cancelledRequestsGrid.jsx columns:
//   Ref#, Defendant, Posting Agency*, Reason, Date Cancelled, Details
// ============================================================

const CancelledRequestCard = ({ row, onView, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const { date, time } = formatDateTime(row.cancelledDate);

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
      onClick={() => onView(row)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onView(row); }}
          className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors flex-shrink-0" aria-label="View details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Reason</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.cancelledReason || 'N/A'}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Date Cancelled</p><p className="text-sm font-medium text-gray-900 dark:text-white">{date}{time ? ` ${time}` : ''}</p></div>
        {canViewPostingAgency && (
          <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Posting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.postingAgencyName || '-'}</p></div>
        )}
        <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Details</p><p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{row.cancelledReason || 'No details provided'}</p></div>
      </div>
    </div>
  );
};

const CancelledTableRow = ({ row, onView, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const { date, time } = formatDateTime(row.cancelledDate);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[180px] truncate">{defendantName}</td>
      {canViewPostingAgency && (
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.postingAgencyName || '-'}</td>
      )}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.cancelledReason || 'N/A'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        <div className="leading-tight">{date}</div>
        {time && <div className="text-xs text-gray-400 dark:text-gray-500">{time}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[220px] truncate">{row.cancelledReason || 'No details provided'}</td>
      <td className="px-4 py-3 text-sm">
        <button onClick={(e) => { e.stopPropagation(); onView(row); }}
          className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

// ============================================================
// Closed Tab: Card + Row components
// Matches legacy closedRequestGrid.jsx columns:
//   Ref#, Defendant, Posting Agency*, Created At, Origin, Amount, County, State
// ============================================================

const ClosedRequestCard = ({ row, onView, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || 'Unknown';
  const location = [row.county, row.state].filter(Boolean).join(', ') || '-';
  const origin = row.isOnline === true ? 'Online' : 'Bankers In-House';

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
      onClick={() => onView(row)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">{defendantName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Ref #{row.transferBondTransactionID}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onView(row); }}
          className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors flex-shrink-0" aria-label="View details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Location</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Amount</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Origin</p><p className="text-sm font-medium text-gray-900 dark:text-white italic">{origin}</p></div>
        <div><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Created</p><p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(row.createdAt)}</p></div>
        {canViewPostingAgency && (
          <div className="col-span-2"><p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Posting Agency</p><p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.postingAgencyName || '-'}</p></div>
        )}
      </div>
    </div>
  );
};

const ClosedTableRow = ({ row, onView, canViewPostingAgency }) => {
  const defendantName = `${row.defendantFirstName || ''} ${row.defendantLastName || ''}`.trim() || '-';
  const { date, time } = formatDateTime(row.createdAt);
  const origin = row.isOnline === true ? 'Online' : 'Bankers In-House';

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors" onClick={() => onView(row)}>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">#{row.transferBondTransactionID}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[180px] truncate">{defendantName}</td>
      {canViewPostingAgency && (
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{row.postingAgencyName || '-'}</td>
      )}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        <div className="leading-tight">{date}</div>
        {time && <div className="text-xs text-gray-400 dark:text-gray-500">{time}</div>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 italic">{origin}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatCurrency(row.totalBondAmount)}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{row.county || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{row.state || '-'}</td>
      <td className="px-4 py-3 text-sm">
        <button onClick={(e) => { e.stopPropagation(); onView(row); }}
          className="p-1.5 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors" title="View Details">
          <EyeIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

// ============================================================
// Main Component
// ============================================================

const RequestingList = ({ agencyId }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canViewPostingAgency = can('READ:TRANSFER-BOND-POSTING-AGENCY');
  const isMobile = useIsMobile();
  const [subTab, setSubTab] = useState(() => sessionStorage.getItem('tb-requesting-subTab') || 'active');
  const [loading, setLoading] = useState(false);
  const [closedLoading, setClosedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const PAGE_SIZE = 10;

  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Per-group pagination state: { [agencyName]: pageNumber }
  const [groupPages, setGroupPages] = useState({});

  const [activeRequests, setActiveRequests] = useState([]);
  const [cancelledRequests, setCancelledRequests] = useState([]);
  const [closedRequests, setClosedRequests] = useState([]);
  const [closedHasSearched, setClosedHasSearched] = useState(false);

  // Cancelled: recent/all toggle (matches legacy cancelledRequestsGrid.jsx)
  const [showAllCancelled, setShowAllCancelled] = useState(false);
  // Cancelled date range filters (client-side, only when showAll)
  const [cancelledStartDate, setCancelledStartDate] = useState('');
  const [cancelledEndDate, setCancelledEndDate] = useState('');

  // Closed: date range for API search
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchActiveRequests = useCallback(async (silent = false) => {
    if (!agencyId) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await getTransferBondRequests();
      if (response.success) {
        const active = (response.data || []).filter((r) => r.isDeferred !== 'Y' && r.statusCd !== 'C');
        setActiveRequests(active);
      } else if (!silent) {
        setError(response.error || 'Failed to load requests');
      }
    } catch {
      if (!silent) setError('Failed to load requests');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [agencyId]);

  const fetchCancelledRequests = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getCancelledRequests(showAllCancelled);
      if (response.success) {
        setCancelledRequests(response.data || []);
      } else {
        setError(response.error || 'Failed to load cancelled requests');
      }
    } catch {
      setError('Failed to load cancelled requests');
    } finally {
      setLoading(false);
    }
  }, [agencyId, showAllCancelled]);

  const fetchClosedRequests = useCallback(async () => {
    if (!agencyId) return;

    // 90-day max validation (matches legacy closedRequestGrid.jsx)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffInDays = (end - start) / (1000 * 60 * 60 * 24);

    if (start > end) {
      setError('Start date cannot be after end date.');
      return;
    }
    if (diffInDays > 90) {
      setError('Date range should not exceed 90 days.');
      return;
    }

    setClosedLoading(true);
    setError(null);
    try {
      const response = await getClosedRequests(
        start.toISOString(),
        end.toISOString()
      );
      if (response.success) {
        setClosedRequests(response.data || []);
        setClosedHasSearched(true);
      } else {
        setError(response.error || 'Failed to load closed requests');
      }
    } catch {
      setError('Failed to load closed requests');
    } finally {
      setClosedLoading(false);
    }
  }, [agencyId, startDate, endDate]);

  // Auto-fetch for active and cancelled tabs; closed loads recent 5 by default
  useEffect(() => {
    setGroupPages({});
    if (subTab === 'active') fetchActiveRequests();
    else if (subTab === 'cancelled') fetchCancelledRequests();
    else if (subTab === 'closed' && closedRequests.length === 0 && !closedHasSearched) fetchClosedRequests();
  }, [subTab, fetchActiveRequests, fetchCancelledRequests, fetchClosedRequests, closedRequests.length, closedHasSearched]);

  // Auto-refresh active requests every 10 seconds so status updates (e.g. new quotes) appear
  useEffect(() => {
    if (subTab !== 'active') return;
    const interval = setInterval(() => {
      fetchActiveRequests(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [subTab, fetchActiveRequests]);

  // Reset group pages when data changes
  useEffect(() => { setGroupPages({}); }, [activeRequests, cancelledRequests, closedRequests]);

  const handleView = (row) => navigate(`/app/transfer-bonds/request/${row.transferBondTransactionID}`);
  const handleCancel = (row) => navigate(`/app/transfer-bonds/request/${row.transferBondTransactionID}?action=cancel`);

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setGroupPages({});
  };

  // Sort comparator function
  const sortRows = (rows) => {
    if (!sortField) return rows;

    return [...rows].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'transferBondTransactionID':
          aVal = a.transferBondTransactionID || 0;
          bVal = b.transferBondTransactionID || 0;
          break;
        case 'defendant':
          aVal = `${a.defendantFirstName || ''} ${a.defendantLastName || ''}`.toLowerCase();
          bVal = `${b.defendantFirstName || ''} ${b.defendantLastName || ''}`.toLowerCase();
          break;
        case 'location':
          aVal = `${a.county || ''} ${a.state || ''}`.toLowerCase();
          bVal = `${b.county || ''} ${b.state || ''}`.toLowerCase();
          break;
        case 'totalBondAmount':
          aVal = a.totalBondAmount || 0;
          bVal = b.totalBondAmount || 0;
          break;
        case 'numberOfBonds':
          aVal = a.numberOfBonds || 0;
          bVal = b.numberOfBonds || 0;
          break;
        case 'createdAt':
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'cancelledDate':
          aVal = a.cancelledDate ? new Date(a.cancelledDate).getTime() : 0;
          bVal = b.cancelledDate ? new Date(b.cancelledDate).getTime() : 0;
          break;
        default:
          aVal = a[sortField];
          bVal = b[sortField];
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Get current tab data with client-side filters
  const getCurrentData = () => {
    switch (subTab) {
      case 'cancelled': {
        let data = cancelledRequests;
        // Client-side date range filter when Show All is active (matches legacy)
        if (showAllCancelled && cancelledStartDate && cancelledEndDate) {
          const start = new Date(cancelledStartDate);
          const end = new Date(cancelledEndDate);
          end.setHours(23, 59, 59, 999);
          data = data.filter((r) => {
            if (!r.cancelledDate) return false;
            const d = new Date(r.cancelledDate);
            return d >= start && d <= end;
          });
        }
        return data;
      }
      case 'closed': return closedHasSearched ? closedRequests : closedRequests.slice(0, 5);
      default: return activeRequests;
    }
  };

  const rows = getCurrentData();

  // Search filter (covers defendant, county, state, ref# — also cancelledReason for cancelled tab)
  const filteredRows = searchTerm
    ? rows.filter((row) => {
        const s = searchTerm.toLowerCase();
        return (
          (row.defendantFirstName || '').toLowerCase().includes(s) ||
          (row.defendantLastName || '').toLowerCase().includes(s) ||
          (row.county || '').toLowerCase().includes(s) ||
          (row.state || '').toLowerCase().includes(s) ||
          String(row.transferBondTransactionID).includes(searchTerm) ||
          (row.cancelledReason || '').toLowerCase().includes(s)
        );
      })
    : rows;

  // Apply sorting
  const sortedRows = sortRows(filteredRows);

  // Group by agency (matches legacy — all tabs group by dbaAgencyName)
  const groupByAgency = (data) => {
    const groups = {};
    data.forEach((item) => {
      const key = item.dbaAgencyName || 'Unknown Agency';
      if (!groups[key]) {
        groups[key] = {
          dbaAgencyName: key,
          primaryContact: item.primaryContact || '',
          agentId: item.agentId || '',
          records: [],
        };
      }
      groups[key].records.push(item);
    });
    return Object.values(groups);
  };

  const agencyGroups = groupByAgency(sortedRows);

  // Per-group pagination helpers
  const getGroupPage = (groupName) => groupPages[groupName] || 1;
  const setGroupPage = (groupName, page) => setGroupPages((prev) => ({ ...prev, [groupName]: page }));

  const tabs = [
    { id: 'active', label: 'Open Requests', count: activeRequests.length },
    { id: 'closed', label: 'Closed Requests', count: null },
    { id: 'cancelled', label: 'Cancelled Requests', count: null },
  ];

  const isLoading = loading || (subTab === 'closed' && closedLoading);

  // Empty state message
  const getEmptyMessage = () => {
    if (subTab === 'active') return 'No open requests found.';
    if (subTab === 'cancelled') return searchTerm ? 'No requests found for the given criteria.' : 'No cancelled requests found.';
    if (subTab === 'closed') {
      if (closedRequests.length === 0 && !closedLoading) return 'No recent closed requests found. Use the date range to search for older records.';
      return 'No closed requests found.';
    }
    return 'No requests found.';
  };

  // Render tab-specific table headers
  const renderTableHeaders = () => {
    if (subTab === 'active') {
      return (
        <tr>
          <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          <SortableHeader label="Created" field="createdAt" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          <SortableHeader label="Bonds" field="numberOfBonds" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
          <SortableHeader label="Amount" field="totalBondAmount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          {canViewPostingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Posting Agency</th>}
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">County</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">State</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
        </tr>
      );
    }

    if (subTab === 'cancelled') {
      return (
        <tr>
          <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          {canViewPostingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Posting Agency</th>}
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Reason</th>
          <SortableHeader label="Date Cancelled" field="cancelledDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Details</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
        </tr>
      );
    }

    // Closed
    return (
      <tr>
        <SortableHeader label="Ref" field="transferBondTransactionID" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        <SortableHeader label="Defendant" field="defendant" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        {canViewPostingAgency && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Posting Agency</th>}
        <SortableHeader label="Created" field="createdAt" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Origin</th>
        <SortableHeader label="Amount" field="totalBondAmount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">County</th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">State</th>
        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
      </tr>
    );
  };

  // Render tab-specific table row
  const renderTableRow = (row) => {
    if (subTab === 'active') {
      return <OpenTableRow key={row.transferBondTransactionID} row={row} onView={handleView} onCancel={handleCancel} canViewPostingAgency={canViewPostingAgency} />;
    }
    if (subTab === 'cancelled') {
      return <CancelledTableRow key={row.transferBondTransactionID} row={row} onView={handleView} canViewPostingAgency={canViewPostingAgency} />;
    }
    return <ClosedTableRow key={row.transferBondTransactionID} row={row} onView={handleView} canViewPostingAgency={canViewPostingAgency} />;
  };

  // Render tab-specific mobile card
  const renderCard = (row) => {
    if (subTab === 'active') {
      return <OpenRequestCard key={row.transferBondTransactionID} row={row} onView={handleView} onCancel={handleCancel} canViewPostingAgency={canViewPostingAgency} />;
    }
    if (subTab === 'cancelled') {
      return <CancelledRequestCard key={row.transferBondTransactionID} row={row} onView={handleView} canViewPostingAgency={canViewPostingAgency} />;
    }
    return <ClosedRequestCard key={row.transferBondTransactionID} row={row} onView={handleView} canViewPostingAgency={canViewPostingAgency} />;
  };

  return (
    <div>
      {/* Sub-tabs */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-4">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setSubTab(tab.id); sessionStorage.setItem('tb-requesting-subTab', tab.id); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-md transition-all ${
                subTab === tab.id ? 'bg-primary text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${
                  subTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <div className="relative max-w-xs">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by defendant, county, ref#..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setGroupPages({}); }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Cancelled: Recent/All toggle (matches legacy cancelledRequestsGrid.jsx) */}
      {subTab === 'cancelled' && (
        <div className="mb-4 space-y-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={showAllCancelled}
                onChange={(e) => {
                  setShowAllCancelled(e.target.checked);
                  setCancelledStartDate('');
                  setCancelledEndDate('');
                }}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 dark:bg-gray-600 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:bg-primary transition-colors" />
              <div className="absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-4 shadow" />
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-400">
              {showAllCancelled ? 'Viewing All Cancelled Requests' : 'Viewing Recent Cancelled Requests'}
            </span>
          </label>

          {/* Date range filters when Show All (client-side filtering, matches legacy) */}
          {showAllCancelled && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[140px] md:flex-none">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                <input type="date" value={cancelledStartDate} onChange={(e) => setCancelledStartDate(e.target.value)}
                  className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
              <div className="flex-1 min-w-[140px] md:flex-none">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                <input type="date" value={cancelledEndDate} onChange={(e) => setCancelledEndDate(e.target.value)}
                  className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Closed: instruction text + date range search (matches legacy closedRequestGrid.jsx) */}
      {subTab === 'closed' && (
        <div className="mb-4 space-y-3">
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>&#8226; Showing the 5 most recent closed requests by default.</p>
            <p>&#8226; Search within a 90-day date range for more results.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[140px] md:flex-none">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
            <div className="flex-1 min-w-[140px] md:flex-none">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
            <button
              onClick={fetchClosedRequests}
              disabled={closedLoading}
              className="mt-5 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {closedLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Content - grouped by agency (matches legacy) */}
      {isLoading ? (
        isMobile ? <MobileLoadingSkeleton /> : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <TableLoadingSkeleton />
          </div>
        )
      ) : agencyGroups.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">{getEmptyMessage()}</p>
        </div>
      ) : (
        agencyGroups.map((group) => {
          const groupPage = getGroupPage(group.dbaAgencyName);
          const totalGroupPages = Math.ceil(group.records.length / PAGE_SIZE);
          const paginatedRecords = group.records.slice(
            (groupPage - 1) * PAGE_SIZE,
            groupPage * PAGE_SIZE
          );

          return (
            <div key={group.dbaAgencyName} className="mb-6">
              {/* Agency header — Open tab shows name+ID and primary contact; other tabs show name only */}
              <AgencyGroupHeader group={group} showDetails={subTab === 'active'} />

              {isMobile ? (
                // Mobile: cards per group
                <div>
                  {paginatedRecords.map((row) => renderCard(row))}
                  {totalGroupPages > 1 && (
                    <GroupPagination
                      currentPage={groupPage}
                      totalPages={totalGroupPages}
                      totalRows={group.records.length}
                      pageSize={PAGE_SIZE}
                      onPageChange={(p) => setGroupPage(group.dbaAgencyName, p)}
                    />
                  )}
                </div>
              ) : (
                // Desktop: table per group
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        {renderTableHeaders()}
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {paginatedRecords.map((row) => renderTableRow(row))}
                      </tbody>
                    </table>
                  </div>
                  <GroupPagination
                    currentPage={groupPage}
                    totalPages={totalGroupPages}
                    totalRows={group.records.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={(p) => setGroupPage(group.dbaAgencyName, p)}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default RequestingList;
